import {createContext, ReactNode, useContext, useEffect, useMemo, useState} from "react";
import {CreditSummaryResponse, getCreditSummary, getAllCreditSummaries, AllCreditSummaryResponse} from "../utils/creditService";

interface CreditBalance {
  currency: string;
  balance: number;
  used: number;
  available: number;
  id: number;
  available_credit_to_request: number;
  credit_balance: number;
  network: string;
  pending_repayments: number;
  fee_repayment: number;
  pending_fee_repayments: number;
  pending_withdraws: number;
}

interface CreditStatusContextType {
  creditFeesOwed: number;
  availableBalances: CreditBalance[];
  nextPaymentDue: string | null;
  creditTransactions: {
    used: number;
    limit: number;
  };
  selectedCreditLine: number;
  selectedCreditLineId: number | null;
  setSelectedCreditLineId: (id: number) => void;
  allCreditSummaries: AllCreditSummaryResponse;
  isOverdue: boolean;
  paymentPeriod: string;
  isLoading: boolean;
  daysLeft: number;
  overdueDays: number;
  daysInMonth: number;
  creditFeePaymentPeriod: string;
  error: Error | null;
}

const CreditStatusContext = createContext<CreditStatusContextType | undefined>(undefined);

export function CreditStatusProvider({ children }: { children: ReactNode }) {
  const [creditSummary, setCreditSummary] = useState<CreditSummaryResponse | null>(null);
  const [allCreditSummaries, setAllCreditSummaries] = useState<AllCreditSummaryResponse>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [selectedCreditLineId, setSelectedCreditLineId] = useState<number | null>(null);

  // First, fetch all credit summaries to determine available credit lines
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Fetch all credit summaries first
        const data = await getAllCreditSummaries();
        setAllCreditSummaries(data);

        // If no credit line is selected yet, pick the first available one
        if (selectedCreditLineId === null && data.length > 0) {
          const firstCreditLineId = data[0].available_balances.id;
          console.log('Auto-selecting first available credit line:', firstCreditLineId);
          setSelectedCreditLineId(firstCreditLineId);
        }
      } catch (error) {
        console.error('Failed to fetch all credit summaries:', error);
        setError(error as Error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchAllData();
  }, [selectedCreditLineId]);

  // Fetch individual credit line data when selectedCreditLineId changes
  useEffect(() => {
    if (selectedCreditLineId === null) return; // Don't fetch until we have a valid ID
    
    const fetchData = async () => {
      try {
        setError(null);
        console.log('Fetching credit summary for ID:', selectedCreditLineId);
        
        const data = await getCreditSummary(selectedCreditLineId);
        setCreditSummary(data);
      } catch (error) {
        console.error(`Failed to fetch credit summary for ID ${selectedCreditLineId}:`, error);
        setError(error as Error);
      }
    };
    fetchData();
  }, [selectedCreditLineId]);

  const formatDate = (date: string | Date) => {
    const d = new Date(date);
    const month = d.toLocaleString('en-US', { month: 'short' });
    const day = d.getDate();
    const year = d.getFullYear();
    return `${month} ${day} ${year}`;
  };

  const value = useMemo(() => {
    if (!creditSummary || selectedCreditLineId === null) return {
      creditFeesOwed: 0,
      availableBalances: [],
      selectedCreditLine: 0,
      selectedCreditLineId,
      setSelectedCreditLineId,
      nextPaymentDue: "No payment due",
      creditTransactions: { used: 0, limit: 0 },
      paymentPeriod: '',
      isOverdue: false,
      daysLeft: 0,
      overdueDays: 0,
      creditFeePaymentPeriod: '',
      daysInMonth: 0,
      isLoading,
      error,
      allCreditSummaries
    };
    let nextPaymentDue: Date | null = null;
    let dayLeft = 0;
    let calculatedOverdueDays = 0;

    if (creditSummary.next_payment_due != null) {
      nextPaymentDue = new Date(creditSummary.next_payment_due);
      const today = new Date();

      // Set both dates to start of day for fair comparison
      nextPaymentDue.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);

      const timeDiff = today.getTime() - nextPaymentDue.getTime();  // Changed order: today - payment date
      calculatedOverdueDays = Math.floor(timeDiff / (1000 * 60 * 60 * 24));

      if (calculatedOverdueDays > 0) {
        // It's overdue
        dayLeft = 0;
      } else {
        // Not overdue yet
        dayLeft = Math.abs(calculatedOverdueDays);
        calculatedOverdueDays = 0;
      }
    }

    return {
      creditFeesOwed: creditSummary.credit_fees_owed || 0,
      availableBalances: creditSummary.available_balances ? [creditSummary.available_balances] : [],  
      selectedCreditLine: creditSummary.available_balances?.id || 0,  
      selectedCreditLineId,
      setSelectedCreditLineId,
      nextPaymentDue: nextPaymentDue != null ? formatDate(nextPaymentDue) : "No due payment",
      creditFeePaymentPeriod: creditSummary.credit_fee_payment_period || '',
      creditTransactions: creditSummary.credit_transactions || { used: 0, limit: 0 },
      paymentPeriod: creditSummary.credit_fee_payment_period || '',
      isOverdue: calculatedOverdueDays > 0,
      daysLeft: dayLeft,
      overdueDays: calculatedOverdueDays,
      daysInMonth: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate(),
      isLoading,
      error,
      allCreditSummaries
    };
  }, [creditSummary, selectedCreditLineId, setSelectedCreditLineId, isLoading, error, allCreditSummaries]);
  return (
    <CreditStatusContext.Provider value={value}>
      {children}
    </CreditStatusContext.Provider>
  );
}

export function useCredit() {
  const context = useContext(CreditStatusContext);
  if (context === undefined) {
    throw new Error('useCreditStatus must be used within a CreditStatusProvider');
  }
  return context;
}